import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import PrevArrow from 'images/shared/PrevArrow';

const ClientTestimonials = () => {
  const scrollContainerRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const { ipha, genseq, pfizer, alnylam } = useStaticQuery(graphql`
    query {
      ipha: file(relativePath: { eq: "shared/ipha.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      genseq: file(relativePath: { eq: "shared/genseq.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      pfizer: file(relativePath: { eq: "shared/pfizer.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
      alnylam: file(relativePath: { eq: "shared/alnylam.png" }) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH, placeholder: TRACED_SVG, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
  `);

  const testimonials = [
    {
      quote: 'Agency X was our go-to agency for our new start-up Genseq, highly recommended',
      image: genseq,
      name: 'Paul Reid',
      title: 'CEO',
      email: 'paul.reid@genseqgroup.com',
      services: 'FULL BRAND DEVELOPMENT, STRATEGIC EXECUTION AND MANAGEMENT MARKETING'
    },
    {
      quote:
        'Great to work with, bring creativity, digital expertise and technical interactivity to our broadcast events and most recently our annual conference 2025, which was sold out',
      image: ipha,
      name: "Eimear O'Leary",
      title: 'Directory of Communications & Advocacy',
      email: 'eimearoleary@ipha.ie',
      services: 'CREATIVE CAMPAIGN SUPPORT, BROADCAST AND EVENT DELIVERY SINCE 2017'
    },
    {
      quote:
        'Innovative, Creative and excellent client service support - a strategic partner for all campaigns',
      image: pfizer,
      name: 'Caroline Reidy',
      title: 'Strategic Engagement Lead',
      email: 'caroline.reidy@pfizer.com',
      services: 'OMNICHANNEL CAMPAIGNS, DIGITAL STRATEGY AND EVENTS SUPPORT'
    },
    {
      quote: 'A key part of our Irish Business success - highly recommended',
      image: alnylam,
      name: 'Philippa Quigley',
      title: 'Country Lead',
      email: 'pquigley@alnylam.com',
      services: 'FULL OMNICHANNEL CAMPAIGN WORK FOR 5 YEARS + ROI, NI AND SCOTLAND MARKETS'
    }
  ];

  const scrollByViewport = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const viewportWidth = container.clientWidth;
    const amount = viewportWidth * 0.8 * direction; // move by 80% of visible width

    container.scrollTo({
      left: container.scrollLeft + amount,
      behavior: 'smooth'
    });
  };

  const updateScrollState = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;

    setCanScrollPrev(scrollLeft > 4);
    setCanScrollNext(scrollLeft < maxScroll - 4);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();

    const handleScroll = () => {
      updateScrollState();
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const nextTestimonial = () => {
    scrollByViewport(1);
  };

  const prevTestimonial = () => {
    scrollByViewport(-1);
  };

  return (
    <Container>
      <HeaderSection>
        <SectionTitle>In our clients&apos; own words…</SectionTitle>
        <NavigationButtons>
          <NavButton onClick={prevTestimonial} disabled={!canScrollPrev}>
            <PrevArrow />
          </NavButton>
          <NavButton onClick={nextTestimonial} disabled={!canScrollNext}>
            <PrevArrow />
          </NavButton>
        </NavigationButtons>
      </HeaderSection>

      <CarouselContainer>
        <ScrollContainer ref={scrollContainerRef}>
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index}>
              <CardContent>
                <Quote>&ldquo;{testimonial.quote}&rdquo;</Quote>
                <ClientInfo>
                  <LogoContainer>
                    <GatsbyImage image={getImage(testimonial.image)} alt={testimonial.name} />
                  </LogoContainer>
                  <ClientDetails>
                    <ClientName>{testimonial.name}</ClientName>
                    <ClientTitle>{testimonial.title}</ClientTitle>
                    <ClientEmail href={`mailto:${testimonial.email}`}>
                      {testimonial.email}
                    </ClientEmail>
                  </ClientDetails>
                </ClientInfo>
                <Services>{testimonial.services}</Services>
              </CardContent>
            </TestimonialCard>
          ))}
        </ScrollContainer>
      </CarouselContainer>
    </Container>
  );
};

const Container = styled.div`
  /* Remove left padding so the carousel can
     reach the viewport edge and visibly overflow */
  padding: 4rem 0;

  @media (min-width: 768px) {
    padding: 6rem 0;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  max-width: 1400px;
  gap: 2rem;
  margin-left: 1.25rem;
  margin-bottom: 1.5rem;
  @media (min-width: 768px) {
    margin-left: 4rem;
  }
  
`;

const SectionTitle = styled.h3`
  font-weight: 900;
  line-height: 1.1;
  font-size: 2.5rem;
  margin: 0;
  background: linear-gradient(135deg, var(--ax-gold) 0%, var(--ax-gold-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (min-width: 768px) {
    font-size: 3.5rem;
  }
`;

const NavButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 2px;
  background: transparent:
  color: ${(props) => (props.disabled ? '#C1BEAC' : 'var(--ax-gold)')};
  border: ${(props) => (props.disabled ? '1px solid #C1BEAC' : '1px solid var(--ax-gold)')};
  font-size: 1.25rem;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:not(:disabled):hover {
    background: var(--ax-gold);
    transform: translateY(-1px);
    svg {
      color: white;
    }
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    color: ${(props) => (props.disabled ? '#C1BEAC' : 'var(--ax-gold)')};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-right: 1.25rem;

  ${NavButton}:nth-child(2) svg {
    transform: rotate(180deg);
  }

  @media screen and (min-width: 768px) {
    margin-right: 4rem;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: visible;
  margin-bottom: 2rem;
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1rem 0;
  overflow-x: auto;
  scroll-behavior: smooth;

  /* Prevent horizontal scroll from bubbling and triggering browser back/forward */
  overscroll-behavior-x: contain;

  /* Hide scrollbar but keep scrolling */
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (min-width: 768px) {
    gap: 2rem;
  }
`;

const TestimonialCard = styled.div`
  min-width: 300px;
  max-width: 300px;
  background: white;
  border-radius: 2px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  &:first-of-type {
    margin-left: 1.25rem;
    @media (min-width: 768px) {
      margin-left: 4rem;
    }
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }

  @media (min-width: 768px) {
    min-width: 450px;
    max-width: 450px;
  }
`;

const CardContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Quote = styled.blockquote`
  font-size: 1.25rem;
  margin: 0 0 1.5rem 0;
  line-height: 1.4;
  color: var(--ax-black);
  flex: 1;
  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const ClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const LogoContainer = styled.div`
  width: 120px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .gatsby-image-wrapper {
    width: 100%;
    height: auto;
  }
`;

const ClientDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClientName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #1e293b;
  margin-bottom: 0.25rem;
`;

const ClientTitle = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const ClientEmail = styled.a`
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Services = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
  line-height: 1.4;
`;

export default ClientTestimonials;
